// 3rd Party
import PropTypes from 'prop-types';
import React, { forwardRef, useState } from 'react';
import { InfoWindow, Marker } from 'react-google-maps';

// Components
import { Div, FlexRow, P } from 'common/styles/oc-styled-components';

import {
  DIMENSIONS,
  useWindowDimensions,
} from '../../../../providers/WindowDimensionsProvider';

// Other
import { INFO_WINDOW_OPTIONS } from '../../constants/google-maps-constants';
import { reviewRatingMean } from '../../helpers/common-helpers';
import ProfileImageCircles from '../ProfileImageCircles/ProfileImageCircles';
import ResultRating from '../ResultRating/ResultRating';

// Component
const MapMarker = forwardRef(
  (
    {
      hasInteraction,
      highlighted,
      index,
      marker,
      onHighlight,
      onSelect,
      selected,
    },
    ref,
  ) => {
    const {
      info: { image, text, reviews, reviewsCount } = {},
      position,
      label,
      isUserLocation,
    } = marker;
    const [showInfoBox, setShowInfoBox] = useState(false);
    const { layout } = useWindowDimensions();

    const isDesktop =
      layout === DIMENSIONS.DESKTOP || layout === DIMENSIONS.WIDESCREEN;

    const getMarkerElement = (element, mkr) =>
      highlighted || selected || !isDesktop && selected
        ? mkr.highlighted[element]
        : mkr[element];

    const handleHover = isHovering => {
      if (!hasInteraction && !isUserLocation) {
        return;
      }
      if (isDesktop) {
        onHighlight(isHovering ? index : null);
        setShowInfoBox(isHovering);
      }
    };

    const handleClick = () => {
      if (!hasInteraction || isUserLocation && isDesktop) {
        return;
      }
      onSelect(index);
    };

    return (
      <Marker
        icon={getMarkerElement('icon', marker)}
        label={
          hasInteraction && label ? getMarkerElement('label', marker) : null
        }
        onMouseOut={() => handleHover(false)}
        onMouseOver={() => handleHover(true)}
        onClick={handleClick}
        position={position}
        ref={ref}
        // If selected or highlghted, render on top of other
        // markers, else render below lower index markers
        zIndex={selected || highlighted ? 10 : 9 - index}
      >
        {showInfoBox
          && (
            <InfoWindow defaultOptions={INFO_WINDOW_OPTIONS}>
              <Div maxWidth="160px">
                <FlexRow>
                  {image
                    && (
                      <ProfileImageCircles
                        height="40px"
                        images={[image]}
                        width="40px"
                        presentationOnly
                      />
                    )
                  }
                  <Div margin={image && '0 0 0 10px'} maxWidth="120px">
                    <P fontWeight="bold">{text}</P>
                  </Div>
                </FlexRow>
                <FlexRow>
                  <Div>
                    <Div margin="5px 0 0 0">
                      {reviewsCount > 0
                        && (
                          <ResultRating
                            reviewsRating={reviewRatingMean(reviews)}
                            reviewsCount={reviewsCount}
                          />
                        )
                      }
                      {!reviewsCount && <P>{marker.address}</P>}
                    </Div>
                  </Div>
                </FlexRow>
              </Div>
            </InfoWindow>
          )
        }
      </Marker>
    );
  },
);

MapMarker.propTypes = {
  hasInteraction: PropTypes.bool,
  highlighted: PropTypes.bool,
  index: PropTypes.number,
  marker: PropTypes.object,
  onHighlight: PropTypes.func,
  onSelect: PropTypes.func,
  selected: PropTypes.bool,
};

export default MapMarker;
